<!-- CookieConsent.svelte - GDPR compliant cookie consent banner -->

<script>
    import { onMount } from 'svelte';
    import cookieManager from '../utils/cookieManager.js';

    let showBanner = $state(false);
    let showDetails = $state(false);
    let consentGiven = $state(false);

    // Consent categories
    let analyticsConsent = $state(true);
    let personalizationConsent = $state(true);

    onMount(() => {
        // Check if user has already made a choice
        const existingConsent = cookieManager.getCookie('fp_consent');
        if (!existingConsent) {
            showBanner = true;
        } else {
            consentGiven = existingConsent === 'granted';
        }
    });

    function acceptAll() {
        cookieManager.setConsent(true);
        consentGiven = true;
        showBanner = false;
        
        // Track consent acceptance
        cookieManager.trackInteraction('consent_accepted', {
            type: 'accept_all',
            analytics: true,
            personalization: true
        });
    }

    function rejectAll() {
        cookieManager.setConsent(false);
        consentGiven = false;
        showBanner = false;
        
        // Only essential cookies remain
        cookieManager.trackInteraction('consent_rejected', {
            type: 'reject_all'
        });
    }

    function saveCustomPreferences() {
        const granted = analyticsConsent || personalizationConsent;
        cookieManager.setConsent(granted);
        
        // Save specific preferences
        cookieManager.setCookie('fp_analytics_consent', analyticsConsent.toString(), { expires: 365 });
        cookieManager.setCookie('fp_personalization_consent', personalizationConsent.toString(), { expires: 365 });
        
        consentGiven = granted;
        showBanner = false;
        showDetails = false;
        
        cookieManager.trackInteraction('consent_customized', {
            analytics: analyticsConsent,
            personalization: personalizationConsent
        });
    }

    function toggleDetails() {
        showDetails = !showDetails;
    }
</script>

{#if showBanner}
    <div class="cookie-banner" role="dialog" aria-labelledby="cookie-banner-title" aria-describedby="cookie-banner-description">
        <div class="cookie-content">
            <div class="cookie-header">
                <h3 id="cookie-banner-title">🍪 We value your privacy</h3>
            </div>
            
            <div class="cookie-body">
                <p id="cookie-banner-description">
                    We use cookies to enhance your experience with Focal Path. Essential cookies help the app function, 
                    while analytics cookies help us improve our decision-making framework.
                </p>
                
                {#if showDetails}
                    <div class="cookie-details">
                        <div class="cookie-category">
                            <h4>Essential Cookies <span class="required">(Required)</span></h4>
                            <p>These cookies are necessary for the website to function and cannot be switched off.</p>
                            <ul>
                                <li>Session management and security</li>
                                <li>Saving your decision progress</li>
                                <li>Basic functionality</li>
                            </ul>
                        </div>
                        
                        <div class="cookie-category">
                            <label class="cookie-toggle">
                                <input type="checkbox" bind:checked={analyticsConsent} />
                                <span class="toggle-slider"></span>
                                <div class="toggle-content">
                                    <h4>Analytics Cookies</h4>
                                    <p>Help us understand how you use Focal Path to improve the experience.</p>
                                    <ul>
                                        <li>Decision completion rates</li>
                                        <li>Feature usage patterns</li>
                                        <li>Performance monitoring</li>
                                    </ul>
                                </div>
                            </label>
                        </div>
                        
                        <div class="cookie-category">
                            <label class="cookie-toggle">
                                <input type="checkbox" bind:checked={personalizationConsent} />
                                <span class="toggle-slider"></span>
                                <div class="toggle-content">
                                    <h4>Personalization Cookies</h4>
                                    <p>Remember your preferences to provide a tailored experience.</p>
                                    <ul>
                                        <li>UI preferences (tips, examples)</li>
                                        <li>Theme and accessibility settings</li>
                                        <li>Decision history patterns</li>
                                    </ul>
                                </div>
                            </label>
                        </div>
                    </div>
                {/if}
            </div>
            
            <div class="cookie-actions">
                {#if !showDetails}
                    <button class="btn-secondary" onclick={toggleDetails}>
                        Customize
                    </button>
                    <button class="btn-secondary" onclick={rejectAll}>
                        Reject All
                    </button>
                    <button class="btn-primary" onclick={acceptAll}>
                        Accept All
                    </button>
                {:else}
                    <button class="btn-secondary" onclick={toggleDetails}>
                        Back
                    </button>
                    <button class="btn-primary" onclick={saveCustomPreferences}>
                        Save Preferences
                    </button>
                {/if}
            </div>
        </div>
    </div>
{/if}

<style>
    .cookie-banner {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        background: white;
        border-top: 3px solid #9CFFFA;
        box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
        z-index: 1000;
        max-height: 80vh;
        overflow-y: auto;
    }

    .cookie-content {
        max-width: 1200px;
        margin: 0 auto;
        padding: 1.5rem;
    }

    .cookie-header h3 {
        margin: 0 0 1rem 0;
        color: #084B83;
        font-size: 1.2rem;
    }

    .cookie-body p {
        margin: 0 0 1rem 0;
        line-height: 1.5;
        color: #333;
    }

    .cookie-details {
        margin-top: 1.5rem;
        border-top: 1px solid #eee;
        padding-top: 1.5rem;
    }

    .cookie-category {
        margin-bottom: 1.5rem;
        padding: 1rem;
        border: 1px solid #eee;
        border-radius: 8px;
        background: #f9f9f9;
    }

    .cookie-category h4 {
        margin: 0 0 0.5rem 0;
        color: #084B83;
        font-size: 1rem;
    }

    .required {
        font-size: 0.8rem;
        color: #666;
        font-weight: normal;
    }

    .cookie-category p {
        margin: 0 0 0.5rem 0;
        font-size: 0.9rem;
        color: #666;
    }

    .cookie-category ul {
        margin: 0;
        padding-left: 1.2rem;
        font-size: 0.85rem;
        color: #777;
    }

    .cookie-category li {
        margin-bottom: 0.2rem;
    }

    .cookie-toggle {
        display: flex;
        align-items: flex-start;
        gap: 1rem;
        cursor: pointer;
    }

    .cookie-toggle input[type="checkbox"] {
        display: none;
    }

    .toggle-slider {
        width: 50px;
        height: 24px;
        background: #ccc;
        border-radius: 12px;
        position: relative;
        transition: background 0.3s;
        flex-shrink: 0;
        margin-top: 0.2rem;
    }

    .toggle-slider::after {
        content: '';
        width: 20px;
        height: 20px;
        background: white;
        border-radius: 50%;
        position: absolute;
        top: 2px;
        left: 2px;
        transition: transform 0.3s;
    }

    .cookie-toggle input:checked + .toggle-slider {
        background: #9CFFFA;
    }

    .cookie-toggle input:checked + .toggle-slider::after {
        transform: translateX(26px);
    }

    .toggle-content {
        flex: 1;
    }

    .cookie-actions {
        display: flex;
        gap: 1rem;
        justify-content: flex-end;
        margin-top: 1.5rem;
        flex-wrap: wrap;
    }

    .btn-primary, .btn-secondary {
        padding: 0.75rem 1.5rem;
        border: none;
        border-radius: 6px;
        cursor: pointer;
        font-size: 0.9rem;
        font-weight: 500;
        transition: all 0.2s;
    }

    .btn-primary {
        background: #9CFFFA;
        color: #084B83;
    }

    .btn-primary:hover {
        background: #FFEC6C;
        transform: translateY(-1px);
    }

    .btn-secondary {
        background: #f5f5f5;
        color: #666;
        border: 1px solid #ddd;
    }

    .btn-secondary:hover {
        background: #e9e9e9;
        color: #333;
    }

    @media (max-width: 768px) {
        .cookie-content {
            padding: 1rem;
        }
        
        .cookie-actions {
            justify-content: stretch;
        }
        
        .btn-primary, .btn-secondary {
            flex: 1;
            min-width: 0;
        }
        
        .cookie-toggle {
            flex-direction: column;
            gap: 0.5rem;
        }
        
        .toggle-slider {
            align-self: flex-start;
        }
    }
</style>
